import { apiUrl } from "../http/apiConfiguration";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readBoolean, readInteger, readNullableString, readObject, readString } from "../http/jsonDecoding";

/** The project's own Git as one Git bundle, for anyone who may read its repositories (F6, ADR-0042). */
export function projectCheckpointUrl(projectId: string): string {
  return apiUrl(`/projects/${encodeURIComponent(projectId)}/checkpoint`);
}

/**
 * Imports through DMW's Git host (`docs/components/content.md` D-18): Cloud creates the
 * hosted source, its mirror and its corpus connection in one call; Local uploads files as
 * the next commit of the corpus's upload source. Either way the connection's update
 * Workflow imports the commit in a Thread.
 */
export type UpdatePolicy = "automatic" | "on_demand";

export interface ImportedSource {
  readonly sourceId: string;
  readonly sourceName: string;
  readonly connectionId: string;
}

export interface CloudImportInput {
  readonly remote: string;
  readonly corpusId: string;
  readonly policy: UpdatePolicy;
  readonly branch: string;
  /** `null`: fetched only when someone asks. */
  readonly intervalSeconds: number | null;
  readonly workflow: string;
  /** The person's external connection a private repository is read through (ADR-0035);
   * `null` for a public one. Importing with it is their consent to copy the repository
   * for the project's members. */
  readonly externalConnectionId: string | null;
}

export interface SourceUpload extends ImportedSource {
  readonly createdSource: boolean;
  /** The upload's commit; `null` when the source held these files already. */
  readonly commit: string | null;
  readonly fileCount: number;
}

const TAR_MEDIA_TYPE = "application/x-tar";

function readImportedSource(value: unknown): ImportedSource {
  const body = readObject(value, "imported source");
  const source = readObject(body["source"], "source");
  return { sourceId: readString(source, "repository_id"), sourceName: readString(source, "name"), connectionId: readString(body, "connection_id") };
}

export function importFromCloud(projectId: string, input: CloudImportInput): Promise<ImportedSource> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/source-imports`,
    body: jsonBody({ remote: input.remote, corpus_id: input.corpusId, policy: input.policy, branch: input.branch, interval_seconds: input.intervalSeconds, workflow: input.workflow, external_connection_id: input.externalConnectionId }),
    decodeResponse: readImportedSource,
  });
}

/** The import Workflow every upload into the corpus runs, pinned by its first upload; `null` before one. */
export function readUploadWorkflow(corpusId: string, signal?: AbortSignal): Promise<string | null> {
  return requestJson({
    method: "GET",
    path: `/corpora/${encodeURIComponent(corpusId)}/source-imports`,
    signal,
    decodeResponse: (value) => (value === null ? null : readString(readObject(value, "upload source"), "workflow")),
  });
}

/** `workflow` is needed only when this upload creates the corpus's upload source. */
export function uploadDocuments(corpusId: string, archive: Blob, workflow: string | null): Promise<SourceUpload> {
  return requestJson({
    method: "POST",
    path: withQuery(`/corpora/${encodeURIComponent(corpusId)}/source-imports`, { workflow }),
    body: { kind: "file", content: archive, contentType: TAR_MEDIA_TYPE },
    decodeResponse: (value) => {
      const body = readObject(value, "upload");
      return { ...readImportedSource(body), createdSource: readBoolean(body, "created_source"), commit: readNullableString(body, "commit"), fileCount: readInteger(body, "file_count") };
    },
  });
}
